import React, { useState, useEffect } from 'react';
import { FiX, FiPlus, FiTrash2 } from 'react-icons/fi';
import { useTask } from '../contexts/TaskContext';
import { useAuth } from '../contexts/AuthContext';
import SplitSubtaskButton from './SplitSubtaskButton';
import '../styles/TaskModal.css';

const TaskModal = ({ isOpen, onClose, task = null }) => {
  const { createTask, updateTask, users, projects } = useTask();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    urgency: 3,
    deadline: '',
    status: 'todo',
    reviewerId: '',
    assignedTo: '',
    assignees: [],
    subtasks: [],
  });
  const [newSubtask, setNewSubtask] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);

  useEffect(() => {
    if (task) {
      // Handle both assignedTo and assignee fields
      const assignedUserId = task.assignedTo || task.assignee?._id || task.assignee || '';
      const assignedUserIds = task.assignees ? task.assignees.map(u => u._id || u) : [];

      setFormData({
        title: task.title || '',
        description: task.description || '',
        urgency: task.urgency || 3,
        deadline: task.deadline ? new Date(task.deadline).toISOString().slice(0, 10) : '',
        status: task.status || 'todo',
        reviewerId: task.reviewerId?._id || task.reviewerId || '',
        assignedTo: assignedUserId,
        assignees: assignedUserIds,
        subtasks: task.subtasks || [],
        projectId: task.projectId?._id || task.projectId || '',
      });
    } else {
      let defaultReviewer = '';
      if (user) {
        if (user.role === 'manager') {
          defaultReviewer = user._id || user.id;
        } else if (user.managerId) {
          defaultReviewer = user.managerId;
        }
      }

      setFormData({
        title: '',
        description: '',
        urgency: 3,
        deadline: '',
        status: 'todo',
        reviewerId: defaultReviewer,
        assignedTo: '',
        assignees: [],
        subtasks: [],
        projectId: '',
      });
    }
  }, [task, isOpen, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const nextData = { ...prev, [name]: value };

      if (name === 'projectId') {
        const selectedProj = projects?.find(p => p._id === value);
        if (selectedProj && selectedProj.managerId) {
          nextData.reviewerId = selectedProj.managerId;
        } else if (user) {
          if (user.role === 'manager') {
            nextData.reviewerId = user._id || user.id;
          } else if (user.managerId) {
            nextData.reviewerId = user.managerId;
          } else {
            nextData.reviewerId = '';
          }
        }
      }

      return nextData;
    });
  };

  const addSubtask = () => {
    if (newSubtask.trim()) {
      setFormData(prev => ({
        ...prev,
        subtasks: [...prev.subtasks, { title: newSubtask, completed: false }],
      }));
      setNewSubtask('');
    }
  };

  const removeSubtask = (index) => {
    setFormData(prev => ({
      ...prev,
      subtasks: prev.subtasks.filter((_, i) => i !== index),
    }));
  };

  const toggleAssignee = (userId) => {
    setFormData(prev => {
      const assignees = [...prev.assignees];
      const index = assignees.indexOf(userId);

      if (index > -1) {
        assignees.splice(index, 1);
      } else {
        assignees.push(userId);
      }

      return { ...prev, assignees };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const trimmedTitle = formData.title.trim();
    const trimmedDesc = formData.description?.trim() || '';

    if (!trimmedTitle || trimmedTitle.length < 3) {
      alert('Title is required and must be at least 3 characters.');
      return;
    }

    if (!formData.reviewerId) {
      alert('A reviewer is required for all tasks!');
      return;
    }

    setIsSubmitting(true);

    const taskData = {
      ...formData,
      title: trimmedTitle,
      description: trimmedDesc,
      urgency: Number(formData.urgency),
      assignees: formData.assignees && formData.assignees.length > 0 ? formData.assignees : [],
      reviewerId: formData.reviewerId || null,
      projectId: formData.projectId || null,
    };

    // Remove assignedTo and assignee fields - we only use assignees now
    delete taskData.assignedTo;

    let result;
    if (task) {
      result = await updateTask(task._id, taskData);
    } else {
      result = await createTask(taskData);
    }

    setIsSubmitting(false);

    if (result.success) {
      onClose();
    }
  };

  const assignReviewerAndSetReview = async () => {
    if (!task) {
      alert('Save the task before assigning a reviewer');
      return;
    }
    if (!formData.reviewerId) {
      alert('Select a reviewer first');
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await updateTask(task._id, { reviewerId: formData.reviewerId, status: 'review' });
      if (result.success) {
        alert('Reviewer assigned and task moved to Review');
        onClose();
      } else {
        alert(result.error || 'Failed to assign reviewer');
      }
    } catch (err) {
      console.error('Assign reviewer failed', err);
      alert('Failed to assign reviewer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSubtask();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{task ? 'Edit Task' : 'Create New Task'}</h2>
          <button className="close-btn" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="task-form">
          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter task title"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter task description"
              rows={4}
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="urgency">Urgency</label>
              <select
                id="urgency"
                name="urgency"
                value={formData.urgency}
                onChange={handleChange}
                disabled={isSubmitting}
              >
                <option value={1}>1 - Minimal</option>
                <option value={2}>2 - Low</option>
                <option value={3}>3 - Medium</option>
                <option value={4}>4 - High</option>
                <option value={5}>5 - Critical</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={isSubmitting}
              >
                <option value="todo">To Do</option>
                <option value="in-progress">In Progress</option>
                <option value="review">Review</option>
                <option value="done">Done</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="deadline">Deadline</label>
              <input
                type="date"
                id="deadline"
                name="deadline"
                value={formData.deadline}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>
            {projects.length > 0 && (
              <div className="form-group">
                <label htmlFor="projectId">Project</label>
                <select
                  id="projectId"
                  name="projectId"
                  value={formData.projectId}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  <option value="">No Project</option>
                  {projects.map(p => (
                    <option key={p._id} value={p._id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Assign To (Multiple)</label>
            <div className="assignees-selection">
              {users.map(u => (
                <div key={u._id} className="assignee-checkbox">
                  <input
                    type="checkbox"
                    id={`assignee-${u._id}`}
                    checked={formData.assignees.includes(u._id)}
                    onChange={() => toggleAssignee(u._id)}
                    disabled={isSubmitting}
                  />
                  <label htmlFor={`assignee-${u._id}`}>
                    {u.username}
                    {u.department && <span className="dept-badge" style={{ fontSize: '10px', marginLeft: '4px', color: '#6b7280' }}>({u.department})</span>}
                    {u.status && <span className={`status-badge ${u.status.toLowerCase().replace(' ', '-')}`}>{u.status}</span>}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* REVIEWER SELECTION */}
          <div className="form-group">
            <label>Reviewer</label>
            <select
              name="reviewerId"
              value={formData.reviewerId}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            >
              <option value="">Select a manager / reviewer...</option>
              {(() => {
                let reviewers = [];
                if (formData.projectId && projects.length > 0) {
                  const proj = projects.find(p => p._id === formData.projectId);
                  if (proj) {
                    if (proj.managerId) reviewers.push(proj.managerId);
                    if (proj.secondManagerId) reviewers.push(proj.secondManagerId);
                    if (proj.hrId) reviewers.push(proj.hrId);
                  }
                } else if (user?.managerId) {
                  reviewers.push(user.managerId);
                } else {
                  // Fallback to all managers/admins if no project and no manager
                  reviewers = users.filter(u => ['manager', 'admin'].includes(u.role));
                }

                // Deduplicate and find user objects
                const uniqueReviewerIds = [...new Set(reviewers.map(r => r._id || r))];
                let options = uniqueReviewerIds.map(id => users.find(u => u._id === id)).filter(Boolean);

                // If options is empty (e.g. user is admin and has no manager, no project), fallback to all managers
                if (options.length === 0) {
                  options = users.filter(u => ['manager', 'admin'].includes(u.role));
                }

                return options.map(u => (
                  <option key={u._id} value={u._id}>{u.username} {u.department ? `(${u.department})` : ''}</option>
                ));
              })()}
            </select>
            <small className="info-message">Assign a reviewer who can approve or request changes.</small>
            <div style={{ marginTop: '0.5rem' }}>
              <button
                type="button"
                className="submit-btn"
                onClick={assignReviewerAndSetReview}
                disabled={isSubmitting || !formData.reviewerId}
              >
                Assign Reviewer & Move to Review
              </button>
            </div>
          </div>

          {task && (
            <div className="form-group">
              <label>Subtasks</label>
              <div className="subtask-input-group">
                <input
                  type="text"
                  value={newSubtask}
                  onChange={(e) => setNewSubtask(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Add a subtask"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={addSubtask}
                  className="add-subtask-btn"
                  disabled={isSubmitting}
                >
                  <FiPlus size={20} />
                </button>
              </div>

              {formData.subtasks.length > 0 && (
                <div className="subtasks-list">
                  {formData.subtasks.map((subtask, index) => (
                    <div key={index} className="subtask-item subtask-item-with-actions">
                      <span className="subtask-title">{typeof subtask === 'string' ? subtask : subtask.title}</span>
                      <div className="subtask-actions">
                        {task && (
                          <SplitSubtaskButton
                            parentTask={task}
                            subtaskIndex={index}
                            subtask={subtask}
                            onSplit={(newTask) => {
                              // Remove from local form state after split
                              setFormData(prev => ({
                                ...prev,
                                subtasks: prev.subtasks.filter((_, i) => i !== index),
                              }));
                            }}
                            removeAfterSplit={false}
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => removeSubtask(index)}
                          className="remove-subtask-btn"
                          disabled={isSubmitting}
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="cancel-btn"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : task ? 'Update Task' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskModal;
